import { get } from 'svelte/store';
import type { CartPlatform } from '../../constants/cart-plaforms';
import type { CartItems, CartItemsStore } from '../createCartItemsStore';
import { getCartCurrency } from '../getCartCurrency';
import { getCartItemsProductIds } from '../getCartItemsProductIds';
import { getCheckoutContext } from '../getCheckoutContext';
import { Config } from '#lib/constants/config.js';
import { fetchWithRetry } from '$global/utils/fetchWithRetry/fetchWithRetry';
import { getCookie } from '$global/utils/getCookie/getCookie';

export type FetchCartParams = {
	coupon_code?: string | null;
	cartItems: CartItems | undefined;
	extraFields?: Record<
		string,
		object | string | boolean | null | Record<string, string>[] | number
	>;
};

export type FetchCartContext = {
	cartPlatform: CartPlatform;
	cartId: CartItemsStore['cartId'];
};

export const fetchCart = async (
	params: FetchCartParams,
	{ cartId, cartPlatform }: FetchCartContext,
	allowEmptyCarts?: boolean
) => {
	const { cartItems, extraFields, ...restParams } = params;

	if (!cartItems) {
		return;
	}

	const dropConfigId = Object.values(cartItems)[0]?.dropConfigId;

	if (
		!get(cartId) &&
		!getCartItemsProductIds(cartItems).length &&
		!allowEmptyCarts
	) {
		return;
	}

	const requestCartId = get(cartId);

	try {
		const response = await fetchWithRetry(
			`${Config.GraphqlApiDomain}/ecomm/cart`,
			{
				method: 'PATCH',
				headers: {
					'Content-type': 'application/json',
					fbp: getCookie('_fbp'),
				} as HeadersInit,
				body: JSON.stringify({
					...restParams,
					platform: +cartPlatform,
					id: requestCartId,
					currency: getCartCurrency(cartItems),
					products: getCartItemsProductIds(cartItems)
						.sort(
							(cartItemKeyA, cartItemKeyB) =>
								(cartItems[cartItemKeyA].sort || 0) -
								(cartItems[cartItemKeyB].sort || 0)
						)
						.map((cartItemKey) => ({
							product: cartItemKey,
							quantity: cartItems[cartItemKey].quantity,
						})),
					...(extraFields || {}),
					...(dropConfigId && {
						drop_config: +dropConfigId,
					}),
				}),
			}
		);

		const jsonResponse = await response.json();

		if (response.ok) {
			// The cart may have been cleared while the request was in flight (the
			// order-confirmed page does so on mount). Writing the response id back
			// then would resurrect the cart of an order that is already processed.
			if (get(cartId) === requestCartId && requestCartId !== jsonResponse.id) {
				cartId.set(jsonResponse.id);
			}

			return Promise.resolve(jsonResponse);
		} else {
			return Promise.reject(jsonResponse?.errors?.[0]);
		}
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
	} catch (e: any) {
		return Promise.reject(e.message);
	}
};

export const upsertCart = async (params: FetchCartParams) => {
	const {
		cartPlatform,
		cartItems: { cartId },
	} = getCheckoutContext();

	const response = await fetchCart(params, { cartPlatform, cartId });
	return response;
};
